import { type ServerResponse, axios } from '~/axios'
import { type StoplightColorValue } from '~/utils/types/surveys'

export type SnapshotDraft = Awaited<
  ReturnType<typeof getDrafts>
>['data']['data']['getSnapshotDraft'][number]

export async function getDrafts() {
  interface SnapshotDraft {
    stoplightAlreadyAttached: boolean
    snapshotId: number | null
    snapshotDraftDate: number
    draftId: string
    lifemapNavHistory: LifemapNavHistory[]
    surveyId: number
    project: number | null
    economicTime: number
    stoplightTime: number
    lifemapTime: number
    justStoplight: boolean | null
    surveyVersionId: null
    snapshotStoplightAchievements: SnapshotStoplightAchievement[]
    snapshotStoplightPriorities: SnapshotStoplightPriority[]
    indicatorSurveyDataList: IndicatorSurveyDataList[]
    economicSurveyDataList: EconomicSurveyDataList[]
    familyDataDTO: FamilyData
  }

  interface EconomicSurveyDataList {
    key: string
    value: null | string
    multipleValue: string[] | null
    other: null | string
  }

  interface FamilyData {
    familyId: number | null
    countFamilyMembers: number
    latitude: number | null
    longitude: number | null
    country: string | null
    familyMemberDTOList: FamilyMember[]
  }

  interface FamilyMember {
    firstParticipant: boolean
    firstName: string
    lastName: string | null
    birthCountry: null | string
    gender: string
    customGender: null
    birthDate: number
    documentType: null | string
    customDocumentType: null
    documentNumber: null | string
    email: null
    phoneCode: null | string
    phoneNumber: null | string
    socioEconomicAnswers: SocioEconomicAnswer[]
  }

  interface SocioEconomicAnswer {
    key: string
    value: string | null
    other: string | null
    multipleValue: string[] | null
  }

  interface IndicatorSurveyDataList {
    key: string
    value: StoplightColorValue
  }

  interface LifemapNavHistory {
    url: string
    state: string
  }

  interface SnapshotStoplightAchievement {
    action: string
    indicator: string
    roadmap: string
  }

  interface SnapshotStoplightPriority {
    reason: string
    action: string
    indicator: string
    estimatedDate: number
  }

  const query = /* GraphQL */ `
    query {
      getSnapshotDraft {
        stoplightAlreadyAttached
        snapshotId
        snapshotDraftDate
        draftId
        lifemapNavHistory {
          url
          state
        }
        surveyId
        project
        economicTime
        stoplightTime
        lifemapTime
        justStoplight
        surveyVersionId
        snapshotStoplightAchievements {
          action
          indicator
          roadmap
        }
        snapshotStoplightPriorities {
          reason
          action
          indicator
          estimatedDate
        }
        indicatorSurveyDataList {
          key
          value
        }
        economicSurveyDataList {
          key
          value
          multipleValue
          other
        }
        familyDataDTO {
          familyId
          countFamilyMembers
          latitude
          longitude
          country
          familyMemberDTOList {
            firstParticipant
            firstName
            lastName
            birthCountry
            gender
            customGender
            birthDate
            documentType
            customDocumentType
            documentNumber
            email
            phoneCode
            phoneNumber
            socioEconomicAnswers {
              key
              value
              other
              multipleValue
            }
          }
        }
      }
    }
  `

  return await axios<ServerResponse<{ getSnapshotDraft: SnapshotDraft[] }>>({
    method: 'POST',
    url: '/graphql',
    data: { query },
  })
}

export async function deleteDraftsByIds(draftIds: string[]) {
  const query = /* GraphQL */ `
    mutation deleteDrafts($draftIds: [String]) {
      deleteDrafts(drafts: $draftIds)
    }
  `

  return await axios<ServerResponse<never>>({
    method: 'post',
    url: '/graphql',
    data: { query, variables: { draftIds } },
  })
}
